import React from "react";
import "./ui.css";

/* ─── Button ───────────────────────────────────────── */
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  children,
  className = "",
  disabled,
  ...rest
}) => (
  <button
    className={`btn btn--${variant} btn--${size} ${fullWidth ? "btn--full" : ""} ${className}`}
    disabled={disabled || loading}
    {...rest}
  >
    {loading ? <span className="btn__spinner" /> : children}
  </button>
);

/* ─── Input ────────────────────────────────────────── */
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, className = "", ...rest }, ref) => (
    <div className={`field ${error ? "field--error" : ""} ${className}`}>
      {label && <label className="field__label">{label}</label>}
      <input ref={ref} className="field__input" {...rest} />
      {error && <span className="field__error">{error}</span>}
    </div>
  )
);
Input.displayName = "Input";

/* ─── Select ───────────────────────────────────────── */
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: { value: string; label: string }[];
}

export const Select: React.FC<SelectProps> = ({ label, error, options, className = "", ...rest }) => (
  <div className={`field ${error ? "field--error" : ""} ${className}`}>
    {label && <label className="field__label">{label}</label>}
    <select className="field__input field__select" {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    {error && <span className="field__error">{error}</span>}
  </div>
);

/* ─── Textarea ─────────────────────────────────────── */
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const Textarea: React.FC<TextareaProps> = ({ label, error, className = "", ...rest }) => (
  <div className={`field ${error ? "field--error" : ""} ${className}`}>
    {label && <label className="field__label">{label}</label>}
    <textarea className="field__input field__textarea" {...rest} />
    {error && <span className="field__error">{error}</span>}
  </div>
);

/* ─── Alert ────────────────────────────────────────── */
interface AlertProps {
  type: "error" | "success" | "info";
  children: React.ReactNode;
}

export const Alert: React.FC<AlertProps> = ({ type, children }) => (
  <div className={`alert alert--${type}`}>{children}</div>
);

/* ─── Modal ────────────────────────────────────────── */
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, size = "md" }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal modal--${size}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          {title && <h2 className="modal__title">{title}</h2>}
          <button className="modal__close" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </div>
        <div className="modal__body">{children}</div>
      </div>
    </div>
  );
};

/* ─── StepProgress ─────────────────────────────────── */
interface StepProgressProps {
  steps: string[];
  current: number;
}

export const StepProgress: React.FC<StepProgressProps> = ({ steps, current }) => (
  <div className="steps">
    {steps.map((label, i) => (
      <div
        key={label}
        className={`steps__item ${i < current ? "steps__item--done" : ""} ${i === current ? "steps__item--active" : ""}`}
      >
        <div className="steps__dot">{i < current ? "✓" : i + 1}</div>
        <span className="steps__label">{label}</span>
      </div>
    ))}
    <div className="steps__bar">
      <div className="steps__bar-fill" style={{ width: `${(current / (steps.length - 1)) * 100}%` }} />
    </div>
  </div>
);
